import { useEffect, useState } from "react";
import { FileDown, Sparkles, Loader2, FileText } from "lucide-react";
import { api, apiErr, openPdf } from "@/lib/api";
import { toast } from "sonner";
import { useFiscalYear } from "@/context/FiscalYearContext";
import { PageHeader, Loading, AiDraftNotice } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export default function Reports() {
  const { fiscalYear } = useFiscalYear();
  const [trainings, setTrainings] = useState([]);
  const [offices, setOffices] = useState([]);
  const [officeId, setOfficeId] = useState("");
  const [reflTraining, setReflTraining] = useState("");
  const [ai, setAi] = useState(null);
  const [aiRefl, setAiRefl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reflLoading, setReflLoading] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!fiscalYear) return;
    Promise.all([
      api.get(`/trainings?fiscal_year=${fiscalYear}`),
      api.get("/offices"),
    ]).then(([t, o]) => { setTrainings(t.data); setOffices(o.data); if (o.data.length) setOfficeId(o.data[0].id); setReady(true); });
  }, [fiscalYear]);

  const summary = async () => {
    setLoading(true); setAi(null);
    try { const { data } = await api.post("/ai/annual-summary", { fiscal_year: fiscalYear, office_id: officeId }); setAi(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const reflAnalysis = async () => {
    if (!reflTraining) { toast.error("研修を選択してください"); return; }
    setReflLoading(true); setAiRefl(null);
    try { const { data } = await api.post("/ai/reflection-analysis", { training_id: reflTraining }); setAiRefl(data); }
    catch (e) { toast.error(apiErr(e, "所感がない研修は分析できません")); } finally { setReflLoading(false); }
  };

  if (!ready) return <Loading />;

  return (
    <div>
      <PageHeader title="帳票" subtitle={`${fiscalYear}年度のPDF帳票出力・AIレポート`} />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4"><FileText className="w-4 h-4 text-blue-600" /><h3 className="font-semibold text-slate-800">PDF帳票</h3></div>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Select value={officeId} onValueChange={setOfficeId}><SelectTrigger className="flex-1" data-testid="report-office"><SelectValue /></SelectTrigger>
                <SelectContent>{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
              <Button variant="outline" onClick={() => openPdf(`/reports/annual-plan?fiscal_year=${fiscalYear}&office_id=${officeId}`)} data-testid="report-annual-plan"><FileDown className="w-4 h-4 mr-1" /> 年間計画</Button>
            </div>
            <div className="border-t pt-3">
              <div className="text-sm text-slate-500 mb-2">研修実施記録</div>
              {trainings.map((t) => (
                <div key={t.id} className="flex items-center justify-between py-1.5">
                  <span className="text-sm text-slate-700">{t.name}</span>
                  <Button size="sm" variant="ghost" onClick={() => openPdf(`/reports/training-record/${t.id}`)} data-testid={`report-training-${t.id}`}><FileDown className="w-4 h-4" /></Button>
                </div>
              ))}
              {!trainings.length && <div className="text-xs text-slate-400">研修がありません</div>}
            </div>
            <p className="text-xs text-slate-400 pt-1">※ 委員会議事録・訓練実施記録・修了証のPDFは各画面から出力できます。</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-amber-500" /><h3 className="font-semibold text-slate-800">AI年度総括</h3></div>
            <p className="text-sm text-slate-500 mb-3">年度内の研修・委員会・訓練を集約して総括案を作成します。</p>
            <Button onClick={summary} disabled={loading} className="bg-amber-500 hover:bg-amber-600" data-testid="ai-summary-btn">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "年度総括を生成"}</Button>
            {ai && (
              <div className="mt-4 space-y-3">
                <AiDraftNotice />
                {ai.data ? (
                  <>
                    <div><div className="text-xs font-semibold text-slate-500">概要</div><p className="text-sm text-slate-700 whitespace-pre-wrap">{ai.data.overview}</p></div>
                    {ai.data.achievements && <div><div className="text-xs font-semibold text-slate-500">成果</div><ul className="list-disc pl-5 text-sm text-slate-700">{ai.data.achievements.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
                    {ai.data.issues && <div><div className="text-xs font-semibold text-slate-500">課題</div><ul className="list-disc pl-5 text-sm text-slate-700">{ai.data.issues.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
                  </>
                ) : <pre className="text-xs bg-slate-50 p-3 rounded whitespace-pre-wrap">{ai.raw}</pre>}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-amber-500" /><h3 className="font-semibold text-slate-800">AI所感分析</h3></div>
            <div className="flex gap-2">
              <Select value={reflTraining} onValueChange={setReflTraining}><SelectTrigger className="flex-1" data-testid="refl-training"><SelectValue placeholder="研修を選択" /></SelectTrigger>
                <SelectContent>{trainings.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select>
              <Button onClick={reflAnalysis} disabled={reflLoading} className="bg-amber-500 hover:bg-amber-600 shrink-0" data-testid="ai-refl-btn">{reflLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "分析"}</Button>
            </div>
            {aiRefl && (
              <div className="mt-4 space-y-3">
                <AiDraftNotice /><span className="text-xs text-slate-400 ml-2">対象：{aiRefl.count}名（匿名化集計）</span>
                {aiRefl.data ? (
                  <>
                    <div><div className="text-xs font-semibold text-slate-500">理解度の傾向</div><p className="text-sm text-slate-700">{aiRefl.data.understanding_level}</p></div>
                    {aiRefl.data.common_issues && <div><div className="text-xs font-semibold text-slate-500">共通課題</div><ul className="list-disc pl-5 text-sm text-slate-700">{aiRefl.data.common_issues.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
                    {aiRefl.data.next_training_candidates && <div><div className="text-xs font-semibold text-slate-500">次回研修候補</div><ul className="list-disc pl-5 text-sm text-slate-700">{aiRefl.data.next_training_candidates.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
                  </>
                ) : <pre className="text-xs bg-slate-50 p-3 rounded whitespace-pre-wrap">{aiRefl.raw}</pre>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
